import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import FAQ from './pages/FAQ';
import Proposals from './pages/Proposals';
import Newsletter from './pages/Newsletter';
import AIInstructions from './pages/AIInstructions';
import Appointments from './pages/Appointments';
import Settings from './pages/Settings';
import DestinationLibrary from './pages/DestinationLibrary';
import Chat from './pages/Chat';
import Bookings from './pages/Bookings';
import Reisbescheiden from './pages/Reisbescheiden';
import PublicBooking from './pages/PublicBooking';
import PublicProposal from './pages/PublicProposal';
import AccountPage from './pages/AccountPage';
import Knowledge from './pages/Knowledge';
import Klantinzichten from './pages/Klantinzichten';
import Pricing from './pages/Pricing';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
      <div className="text-center">
        <div className="spinner spinner-blue mx-auto mb-3" style={{ width: 32, height: 32, borderWidth: 3 }} />
        <p className="text-gray-500 text-sm">Laden...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { isAdmin, loading, isAdminLoading } = useAuth();
  if (loading || isAdminLoading) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
        <BrowserRouter>
          <Routes>
            {/* ── Auth ── */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login"    element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

            {/* ── Public proposal / booking pages ── */}
            <Route path="/book/:agentSlug"  element={<PublicBooking />} />
            <Route path="/proposal/:token"  element={<PublicProposal />} />

            {/* ── Protected app — pathless layout wrapper ── */}
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/dashboard"          element={<Dashboard />} />
              <Route path="/faq"                element={<FAQ />} />
              <Route path="/proposals"          element={<Proposals />} />
              <Route path="/newsletter"         element={<Newsletter />} />
              <Route path="/appointments"       element={<Appointments />} />
              <Route path="/ai-instructions"    element={<AIInstructions />} />
              <Route path="/settings"           element={<Settings />} />
              <Route path="/destination-library" element={<DestinationLibrary />} />
              <Route path="/bookings"           element={<Bookings />} />
              <Route path="/reisbescheiden"     element={<Reisbescheiden />} />
              <Route path="/chat"               element={<Chat />} />
              <Route path="/account"            element={<AccountPage />} />
              <Route path="/knowledge"          element={<AdminRoute><Knowledge /></AdminRoute>} />
              <Route path="/klantinzichten"     element={<Klantinzichten />} />
              <Route path="/pricing"            element={<Pricing />} />
            </Route>

            {/* ── Catch-all ── */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
